import {DateRange} from 'rsuite/DateRangePicker';

import {
    dateFilterKeys,
    dateRangeToFilters,
    setSearchParamsFromFilters,
    UrlFiltersData,
} from 'shared/lib/filterHelper';

import {getLeafTariffIds} from './getLeafTariffIds';

export interface IUserFiltersForm {
    email?: Array<{
        type: string;
        error: string | null;
    }>;
    timezone?: string;
    tariffId?: string[];
    isRecurring?: boolean;
    agreeToNewsletter?: boolean;
    blocked?: string;
    createdAt?: DateRange;
    lastActivityAt?: DateRange;
    activeTo?: DateRange;
    monitorsCountRange?: {min?: number; max?: number};
    incidentsMonitorsCountRange?: {min?: number; max?: number};
    orderMonitor?: Array<{field: 'monitor' | 'incident'; direction: 'asc' | 'desc'}> | undefined;
    orderIncident?: Array<{field: 'monitor' | 'incident'; direction: 'asc' | 'desc'}> | undefined;
}

export const USER_FILTER_KEYS = [
    'blocked',
    ...dateFilterKeys('created_at'),
    ...dateFilterKeys('last_activity_at'),
    ...dateFilterKeys('active_to'),
];

export function getUsersFilters(searchParams: URLSearchParams) {
    const emails: Array<{ type: string; error: string | null }> = [];
    const emailIndices = new Set<number>();
    searchParams.forEach((_, key) => {
        const match = key.match(/^emails\[(\d+)\]\[(type|error)\]$/);
        if (match) emailIndices.add(Number(match[1]));
    });
    Array.from(emailIndices)
        .sort((a, b) => a - b)
        .forEach((i) => {
            const type = searchParams.get(`emails[${i}][type]`) || '';
            const errorRaw = searchParams.get(`emails[${i}][error]`);
            const error = errorRaw === null || errorRaw === 'null' ? null : errorRaw;
            if (type) emails.push({ type, error });
        });

    const tariffIdsParam = searchParams.get('tariffIds') || '';
    const tariffId = tariffIdsParam
        ? tariffIdsParam.split(',').map((s) => s.trim()).filter(Boolean)
        : [];

    const timezone = searchParams.get('timezone') || undefined;

    const isRecurringParam = searchParams.get('isRecurring');
    const isRecurring = isRecurringParam === undefined || isRecurringParam === null
        ? undefined
        : isRecurringParam === 'true';

    const agreeParam = searchParams.get('agreeToNewsletter');
    const agreeToNewsletter = agreeParam === undefined || agreeParam === null
        ? undefined
        : agreeParam === 'true';

    const monitorsCountGte = searchParams.get('monitorsCount[gte]');
    const monitorsCountLte = searchParams.get('monitorsCount[lte]');
    const monitorsCountRange = (monitorsCountGte || monitorsCountLte)
        ? {
            min: monitorsCountGte ? Number(monitorsCountGte) : undefined,
            max: monitorsCountLte ? Number(monitorsCountLte) : undefined,
        }
        : undefined;

    const mwicGte = searchParams.get('monitorsWithIncidentsCount[gte]');
    const mwicLte = searchParams.get('monitorsWithIncidentsCount[lte]');
    const incidentsMonitorsCountRange = (mwicGte || mwicLte)
        ? {
            min: mwicGte ? Number(mwicGte) : undefined,
            max: mwicLte ? Number(mwicLte) : undefined,
        }
        : undefined;

    const order: Array<{ field: 'monitor' | 'incident'; direction: 'asc' | 'desc' }> = [];
    const orderMonitorsCount = searchParams.get('order[monitorsCount]');
    if (orderMonitorsCount === 'asc' || orderMonitorsCount === 'desc') {
        order.push({ field: 'monitor', direction: orderMonitorsCount });
    }
    const orderMWIC = searchParams.get('order[monitorsWithIncidentsCount]');
    if (orderMWIC === 'asc' || orderMWIC === 'desc') {
        order.push({ field: 'incident', direction: orderMWIC });
    }

    return {
        email: emails,
        timezone,
        tariffId,
        isRecurring,
        agreeToNewsletter,
        monitorsCountRange,
        incidentsMonitorsCountRange,
        order: order.length ? order : undefined,
    } as {
        email?: Array<{ type: string; error: string | null }>;
        timezone?: string;
        tariffId?: string[];
        isRecurring?: boolean;
        agreeToNewsletter?: boolean;
        monitorsCountRange?: { min?: number; max?: number };
        incidentsMonitorsCountRange?: { min?: number; max?: number };
        order?: Array<{ field: 'monitor' | 'incident'; direction: 'asc' | 'desc' }>;
    };
}

export function setSearchParamsFromUserFilters(
    searchParams: URLSearchParams,
    data: {
        email?: Array<{ type: string; error: string | null }>;
        timezone?: string;
        tariffId?: string[];
        isRecurring?: boolean | undefined | string;
        agreeToNewsletter?: boolean | undefined | string;
        monitorsCountRange?: { min?: number; max?: number } | string | undefined;
        incidentsMonitorsCountRange?: { min?: number; max?: number } | string | undefined;
        order?: Array<{ field: 'monitor' | 'incident'; direction: 'asc' | 'desc' }>;
    },
) {

    const toBool = (v: unknown): boolean | undefined => {
        if (v === true || v === 'true') return true;
        if (v === false || v === 'false') return false;
        return undefined;
    };

    const toRange = (
        v: unknown,
    ): { min?: number; max?: number } | undefined => {
        if (
            v &&
            typeof v === 'object' &&
            ('min' in (v as any) || 'max' in (v as any))
        ) {
            const obj = v as { min?: number | string; max?: number | string };
            const min = obj.min !== undefined && obj.min !== null && obj.min !== ''
                ? Number(obj.min)
                : undefined;
            const max = obj.max !== undefined && obj.max !== null && obj.max !== ''
                ? Number(obj.max)
                : undefined;
            if (min === undefined && max === undefined) return undefined;
            return { min, max };
        }
        if (typeof v === 'string' && v.trim()) {
            const m = v.match(/(\d+)\D+(\d+)/);
            if (m) {
                return { min: Number(m[1]), max: Number(m[2]) };
            }
            if (/^\d+$/.test(v.trim())) {
                return { min: Number(v.trim()) };
            }
        }
        return undefined;
    };

    const boolIsRecurring = toBool(data.isRecurring as any);
    const boolAgree = toBool(data.agreeToNewsletter as any);
    const rangeMonitors = toRange(data.monitorsCountRange as any);
    const rangeIncidents = toRange(data.incidentsMonitorsCountRange as any);

    const toDelete: string[] = [];
    searchParams.forEach((_, key) => {
        if (/^emails\[\d+\]\[(type|error)\]$/.test(key)) toDelete.push(key);
    });
    toDelete.forEach((k) => searchParams.delete(k));

    if (data.email && data.email.length > 0) {
        data.email.forEach((item, i) => {
            if (item.type) searchParams.set(`emails[${i}][type]`, item.type);
            searchParams.set(
                `emails[${i}][error]`,
                item.error === null || item.error === undefined ? 'null' : String(item.error),
            );
        });
    }

    if (data.tariffId && data.tariffId.length) {
        searchParams.set('tariffIds', data.tariffId.join(','));
    } else {
        searchParams.delete('tariffIds');
    }

    if (data.timezone) searchParams.set('timezone', data.timezone);
    else searchParams.delete('timezone');

    if (typeof boolIsRecurring === 'boolean') {
        searchParams.set('isRecurring', String(boolIsRecurring));
    } else {
        searchParams.delete('isRecurring');
    }

    if (typeof boolAgree === 'boolean') {
        searchParams.set('agreeToNewsletter', String(boolAgree));
    } else {
        searchParams.delete('agreeToNewsletter');
    }

    if (rangeMonitors?.min !== undefined) {
        searchParams.set('monitorsCount[gte]', String(rangeMonitors.min));
    } else {
        searchParams.delete('monitorsCount[gte]');
    }
    if (rangeMonitors?.max !== undefined) {
        searchParams.set('monitorsCount[lte]', String(rangeMonitors.max));
    } else {
        searchParams.delete('monitorsCount[lte]');
    }

    if (rangeIncidents?.min !== undefined) {
        searchParams.set('monitorsWithIncidentsCount[gte]', String(rangeIncidents.min));
    } else {
        searchParams.delete('monitorsWithIncidentsCount[gte]');
    }
    if (rangeIncidents?.max !== undefined) {
        searchParams.set('monitorsWithIncidentsCount[lte]', String(rangeIncidents.max));
    } else {
        searchParams.delete('monitorsWithIncidentsCount[lte]');
    }

    searchParams.delete('order[monitor]');
    searchParams.delete('order[incident]');
    searchParams.delete('order[monitorsCount]');
    searchParams.delete('order[monitorsWithIncidentsCount]');
    if (Array.isArray(data.order)) {
        for (const o of data.order) {
            if (!o || !o.field || !o.direction) continue;
            if (o.direction !== 'asc' && o.direction !== 'desc') continue;
            if (o.field === 'monitor') {
                searchParams.set('order[monitorsCount]', o.direction);
            }
            if (o.field === 'incident') {
                searchParams.set('order[monitorsWithIncidentsCount]', o.direction);
            }
        }
    }
}

export function buildUsersFiltersArray(searchParams: URLSearchParams): string[] {
    const parts: string[] = [];

    searchParams.forEach((value, key) => {
        if (/^emails\[\d+\]\[(type|error)\]$/.test(key)) {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
    });

    const pushIf = (key: string) => {
        const v = searchParams.get(key);
        if (v !== null && v !== '') parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
    };

    pushIf('tariffIds');
    pushIf('timezone');
    pushIf('isRecurring');
    pushIf('agreeToNewsletter');
    pushIf('monitorsCount[gte]');
    pushIf('monitorsCount[lte]');
    pushIf('monitorsWithIncidentsCount[gte]');
    pushIf('monitorsWithIncidentsCount[lte]');
    pushIf('search');
    pushIf('order[monitorsCount]');
    pushIf('order[monitorsWithIncidentsCount]');
    USER_FILTER_KEYS.forEach(pushIf);

    return parts;
}

export function userFiltersToUrlFilters(
    {blocked, createdAt, lastActivityAt, activeTo, orderMonitor, orderIncident, tariffId, ...data}: IUserFiltersForm,
    current: URLSearchParams,
): UrlFiltersData {
    const next = new URLSearchParams(current);

    const order: Array<{ field: 'monitor' | 'incident'; direction: 'asc' | 'desc' }> = [];
    const monitor = orderMonitor?.find(o => o.field === 'monitor');
    if (monitor?.direction) order.push({field: 'monitor', direction: monitor.direction});
    const incident = orderIncident?.find(o => o.field === 'incident');
    if (incident?.direction) order.push({field: 'incident', direction: incident.direction});

    setSearchParamsFromUserFilters(next, {
        ...data,
        order,
        tariffId: getLeafTariffIds(tariffId || []),
    });

    const dates: UrlFiltersData = {blocked: blocked ?? undefined};
    dateRangeToFilters(createdAt, dates, 'created_at');
    dateRangeToFilters(lastActivityAt, dates, 'last_activity_at');
    dateRangeToFilters(activeTo, dates, 'active_to');
    setSearchParamsFromFilters(next, dates);

    const filters: UrlFiltersData = {};
    current.forEach((_, key) => {
        filters[key] = undefined;
    });
    next.forEach((value, key) => {
        filters[key] = value;
    });

    return filters;
}
